import React, {useState, useEffect} from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useGetAllCompaniesQuery } from "../../state/api";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const Companies = () => {
    const theme = useTheme();
    const isNonMediumScreens = useMediaQuery("(min-width: 1200px)");
    const { data: companiesData, isLoading } = useGetAllCompaniesQuery();
    const companies = companiesData?.companies || [];
    const total = companiesData?.total || 0;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchInput, setSearchInput] = useState("");

    const debouncedSearchInput = useDebounce(searchInput, 500);

    const filteredCompanies = companies.filter(company =>
        company.name.toLowerCase().includes(debouncedSearchInput.toLowerCase())
    );

    const columns = [
        {
        field: "id",
        headerName: "ID",
        minWidth: 50,
        flex: isMobile ? 0.2 : 0.3,
        },
        {
        field: "name",
        headerName: "Name",
        minWidth: 180,
        flex: isMobile ? 1 : 1.2,
        },
        {
        field: "phone",
        headerName: "Phone",
        minWidth: 120,
        flex: isMobile ? 0 : 0.7,
        },
        {
        field: "email",
        headerName: "Email",
        minWidth: 110,
        flex: isMobile ? 0 : 0.7,
        },
        {
        field: "billingAddressLine1",
        headerName: "Address",
        minWidth: 120,
        flex: isMobile ? 0 : 0.5,
        },
        {
        field: "billingCountry",
        headerName: "Country",
        minWidth: 90,
        flex: isMobile ? 0 : 0.8,
        },
        {
        field: "totalAmountOfWonDeal",
        headerName: "Total Won deals",
        minWidth: 80,
        flex: isMobile ? 0 : 0.5,
        valueFormatter: (params) => {
            // Parse the value to a float
            const valueNumeric = Number.parseFloat(params.value);
            
            // Use Intl.NumberFormat to format the number in the desired way
            const valueFormatted = new Intl.NumberFormat('de-DE', { 
            style: 'decimal', 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
            }).format(valueNumeric);
            
            return `${valueFormatted} €`;
        }
        }, 
        {
        field: "tags",
        headerName: "Tags",
        minWidth: 50,
        flex: isMobile ? 0 : 0.5,
        },
    ];

    return (
        <Box m="1.5rem 2.5rem">
        <FlexBetween>
            <Header title="COMPANIES" subtitle="Salesmate Companies" />
            <Box>
            </Box>
        </FlexBetween>

        <Box
            mt="20px"
            display="grid"
            gridTemplateColumns="repeat(12, 1fr)"
            gridAutoRows="160px"
            gap="20px"   
            sx={{
            "& > div": { gridColumn: isNonMediumScreens ? undefined : "span 12" },
            }}
        >
            <Box
            height={750} 
            gridColumn="span 12"
            gridRow="span 3"
            sx={{
                "& .MuiDataGrid-root": {
                border: "none",
                borderRadius: "5rem",
                },
                "& .MuiDataGrid-cell": {
                borderBottom: "none",
                width: '100%',
                },
                "& .MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[300],
                borderBottom: "none",
                },
                "& .MuiDataGrid-virtualScroller": {
                backgroundColor: theme.palette.background.alt,
                },
                "& .MuiDataGrid-footerContainer": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[100],
                borderTop: "none",
                },
                "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                color: `${theme.palette.secondary[200]} !important`,
                },
            }}
            >
            <DataGrid
                loading={isLoading || !companiesData}
                getRowId={(company) => company.id}
                rows={filteredCompanies}
                columns={columns}
                rowCount={total}
                components={{
                    Toolbar: () => (
                        <DataGridCustomToolbar
                        setSearchInput={setSearchInput}
                        />
                    )
                }}
            />
            
            </Box>
        </Box>
        </Box>
    );
};

export default Companies;